/**
 * GestorLayout — dedicated layout for the gestor's remesas panel.
 *
 * The sidebar is filtered by the per-gestor `enabled_modules` flags fetched
 * from /api/gestor/me/modules. Always-on entries (Wallet, Mis Pasajeros)
 * are unconditional. WS event `gestor.modules.changed` triggers a refetch
 * and an automatic redirect to `/remesas` if the current page now belongs
 * to a disabled module.
 *
 * Preview mode: when an admin opens this layout via /preview/launch?token=…
 * a sticky red banner is shown and the entire portal is read-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GestorModulesProvider, useGestorModules } from '@/contexts/GestorModulesContext';
import {
  LayoutDashboard, Home, Send, Clock, XCircle, History, CreditCard,
  Users, UserCog, UserSquare2, HelpCircle, MessageCircle, ArrowLeftRight,
  LogOut, ChevronRight, Plane, Ticket, Wallet, Eye, Menu, X, Activity,
} from 'lucide-react';
import NotificationToggle from './NotificationToggle';
import EnablePushBanner from './EnablePushBanner';
import NotificationsBell from './NotificationsBell';
import ConnectivityBanner, { OnlinePill } from '@/components/ConnectivityBanner';
import MobileQuickActionsFab from '@/components/MobileQuickActionsFab';

// Each sidebar entry declares the module key that gates it. Entries without
// `moduleKey` are always visible (Inicio, Dashboard, FAQ — these are landing
// surfaces and shouldn't disappear).
const NAV = [
  { path: '/remesas',                    icon: Home,           label: 'Inicio',                    exact: true },
  { path: '/remesas/dashboard',          icon: LayoutDashboard,label: 'Dashboard' },
  { path: '/remesas/conversor',          icon: ArrowLeftRight, label: 'Conversor',          moduleKey: 'conversor' },
  { path: '/remesas/enviar',             icon: Send,           label: 'Enviar Remesa',      moduleKey: 'remesas' },
  { path: '/remesas/cuentas',            icon: CreditCard,     label: 'Cuentas Bancarias',  moduleKey: 'cuentas_bancarias' },
  { path: '/remesas/pendientes',         icon: Clock,          label: 'Pendientes',         moduleKey: 'pendientes' },
  { path: '/remesas/rechazadas',         icon: XCircle,        label: 'Rechazadas',         moduleKey: 'pendientes' },
  { path: '/remesas/historial',          icon: History,        label: 'Historial',          moduleKey: 'historial' },
  { path: '/remesas/historial-detallado', icon: Activity,      label: 'Historial Detallado',moduleKey: 'historial' },
  { path: '/remesas/tasas-cambio',        icon: ArrowLeftRight, label: 'Tasas de Cambio' },
  { path: '/remesas/promotores',         icon: Users,          label: 'Lista Promotores',   moduleKey: 'promotores' },
  { path: '/remesas/trabajadores',       icon: UserCog,        label: 'Trabajadores',       moduleKey: 'trabajadores' },
  { path: '/remesas/usuarios',           icon: UserSquare2,    label: 'Usuarios' },
  { path: '/remesas/soporte',            icon: MessageCircle,  label: 'Soporte',            moduleKey: 'soporte' },
  { path: '/remesas/faq',                icon: HelpCircle,     label: 'FAQ' },
  // Always-on (pinned)
  { path: '/remesas/mis-pasajeros',      icon: Ticket,         label: 'Mis Pasajeros',      moduleKey: 'mis_pasajeros' },
  { path: '/remesas/mi-wallet',          icon: Wallet,         label: 'Mi Wallet',          moduleKey: 'wallet' },
  { path: '/remesas/wallet-ganancias',   icon: Wallet,         label: 'Mis Ganancias' },
  { path: '/remesas/cuenta-corriente-ceo', icon: Wallet,        label: '🧾 Cuenta CEO' },
];

const PreviewBanner = ({ adminName, mode }) => {
  const isSim = mode === 'simulation';
  return (
    <div
      className={`text-white text-xs sm:text-sm flex items-center justify-center gap-2 py-2 px-3 sticky top-0 z-40 ${
        isSim ? 'bg-amber-600' : 'bg-rose-600'
      }`}
      data-testid="preview-banner"
    >
      <Eye className="w-4 h-4" />
      <span>
        {isSim ? (
          <>
            <b>🧪 Modo Simulación</b> · Tus acciones se persisten con <b>is_simulation=true</b> y NO afectan contabilidad ni reportes reales. Iniciado por <b>{adminName || 'admin'}</b>.
          </>
        ) : (
          <>
            <b>Vista previa</b> · Estás viendo el portal como este gestor (solo lectura). Sesión iniciada por <b>{adminName || 'admin'}</b>.
          </>
        )}
      </span>
    </div>
  );
};

const SidebarInner = () => {
  const { user, logout } = useAuth();
  const { isEnabled, isPreview, previewAdminName, previewMode, ready } = useGestorModules();
  const navigate = useNavigate();
  const location = useLocation();
  // sidebarOpen has two roles:
  //  • Desktop (≥lg): expanded (w-60) vs collapsed (w-16) rail.
  //  • Mobile (<lg): off-canvas drawer (open = visible, closed = hidden).
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopExpanded, setDesktopExpanded] = useState(true);

  const visibleNav = useMemo(
    () => NAV.filter((item) => !item.moduleKey || isEnabled(item.moduleKey)),
    [isEnabled],
  );

  // If the current path's module gets disabled live, bounce to /remesas (Inicio).
  useEffect(() => {
    if (!ready) return;
    const current = NAV.find((n) => {
      if (n.exact) return location.pathname === n.path;
      return location.pathname === n.path || location.pathname.startsWith(`${n.path}/`);
    });
    if (current?.moduleKey && !isEnabled(current.moduleKey)) {
      navigate('/remesas', { replace: true });
    }
  }, [location.pathname, ready, isEnabled, navigate]);

  // Close mobile drawer on route change so navigation feels native.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const onLogout = () => { logout(); navigate('/login'); };

  // On mobile the sidebar is an overlay drawer; on desktop it's a permanent
  // rail that can expand/collapse. We never reserve sidebar-width on mobile.
  return (
    <>
      {isPreview && <PreviewBanner adminName={previewAdminName} mode={previewMode} />}
      <div className="min-h-screen bg-slate-50 lg:flex" data-testid="gestor-layout">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-30"
            onClick={() => setSidebarOpen(false)}
            data-testid="gestor-sidebar-backdrop"
          />
        )}

        <aside
          className={`bg-slate-900 text-slate-100 flex flex-col fixed left-0 z-40 transition-all duration-200
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 lg:static
            w-64 ${desktopExpanded ? 'lg:w-60' : 'lg:w-16'}`}
          style={{ top: isPreview ? '40px' : 0, bottom: 0 }}
        >
          <div className="p-4 border-b border-slate-800 flex items-center gap-2">
            <div className="w-9 h-9 bg-white rounded-lg overflow-hidden shrink-0 ring-2 ring-amber-400">
              <img src="/aircom-logo.png" alt="Aircom" className="w-full h-full object-contain" />
            </div>
            {(desktopExpanded || sidebarOpen) && (
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold leading-none">Aircom Connect</p>
                <p className="text-[10px] text-slate-400 leading-none mt-1">Portal Gestor</p>
              </div>
            )}
            {/* Close-X on mobile */}
            <button
              className="lg:hidden text-slate-400 hover:text-white p-1"
              onClick={() => setSidebarOpen(false)}
              data-testid="gestor-sidebar-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-2" data-testid="gestor-sidebar-nav">
            {visibleNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={!!item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 mx-2 my-0.5 rounded-md text-xs transition-colors
                  ${isActive ? 'bg-amber-400 text-slate-900 font-semibold' : 'text-slate-300 hover:bg-slate-800'}`
                }
                data-testid={`gn${item.path.replace(/\//g, '-')}`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {(desktopExpanded || sidebarOpen) && <span className="truncate">{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          <div className="p-3 border-t border-slate-800 space-y-2">
            {(desktopExpanded || sidebarOpen) && <OnlinePill />}
            {(desktopExpanded || sidebarOpen) && <NotificationToggle />}
            <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-rose-300 hover:bg-rose-900/30" data-testid="gestor-logout">
              <LogOut className="w-4 h-4 shrink-0" />
              {(desktopExpanded || sidebarOpen) && <span>{isPreview ? 'Cerrar vista previa' : 'Cerrar sesión'}</span>}
            </button>
          </div>
        </aside>

        {/* Main column — full-width on mobile, flex-1 on desktop */}
        <div className="flex-1 min-w-0 w-full">
          <ConnectivityBanner />
          <header className="bg-white border-b sticky top-0 z-20">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
              {/* Mobile: open drawer. Desktop: toggle rail. */}
              <button
                onClick={() => {
                  if (window.matchMedia('(min-width: 1024px)').matches) {
                    setDesktopExpanded((s) => !s);
                  } else {
                    setSidebarOpen((s) => !s);
                  }
                }}
                className="text-slate-600 hover:text-slate-900 text-sm flex items-center gap-1 p-2 rounded hover:bg-slate-100"
                data-testid="gestor-sidebar-toggle"
              >
                <Menu className="w-5 h-5 lg:hidden" />
                <ChevronRight className={`w-4 h-4 hidden lg:inline transition-transform ${desktopExpanded ? 'rotate-180' : ''}`} />
                <span className="hidden lg:inline">{desktopExpanded ? 'Ocultar' : 'Mostrar'} menú</span>
              </button>
              <div className="flex items-center gap-2">
                <NotificationsBell />
                <p className="text-xs text-slate-500 text-right truncate min-w-0">
                  Bienvenido, <b className="text-slate-900">{user?.name || user?.email}</b>
                </p>
              </div>
            </div>
          </header>
          <EnablePushBanner audience="gestor" />
          <main className="p-3 sm:p-4 lg:p-6">
            <Outlet />
          </main>
          <MobileQuickActionsFab />
        </div>
      </div>
    </>
  );
};

const GestorLayout = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner w-10 h-10" /></div>;
  if (!user || user.role !== 'gestor') return <Navigate to="/login" replace />;
  return (
    <GestorModulesProvider>
      <SidebarInner />
    </GestorModulesProvider>
  );
};

export default GestorLayout;
